'use client'

import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Label } from '@/shared/components/ui/label'
import { Settings2 } from 'lucide-react'
import errorToast from '@/shared/lib/toast/error.toast'
import { toast } from 'sonner'
import type { BaseRegionResponse } from '@/features/base/types/region-response'
import { authFetch } from '@/shared/lib/auth-fetch'

const START_DAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
]

interface RegionSettingsSectionProps {
  region: BaseRegionResponse
  regionId: number
  onSaved: () => void
}

export function RegionSettingsSection({ region, regionId, onSaved }: RegionSettingsSectionProps) {
  const [defaultCapacity, setDefaultCapacity] = useState<number>(
    region.defaultCapacity ?? 20
  )
  const [startDay, setStartDay] = useState<number>(region.startDay ?? 6)
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    try {
      setIsSaving(true)
      const res = await authFetch(`/regions/${regionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          defaultCapacity,
          startDay,
        }),
      })
      if (!res.ok) {
        const err = await res.text()
        throw new Error(err || 'Failed to save')
      }
      toast.success('Region settings saved')
      onSaved()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save region settings'
      errorToast(msg)
    } finally {
      setIsSaving(false)
    }
  }

  const hasChanges =
    defaultCapacity !== (region.defaultCapacity ?? 20) ||
    startDay !== (region.startDay ?? 6)

  return (
    <div className="rounded-xl border bg-card p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Settings2 className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Region Settings</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Calendar week capacity and week start day for this region.
      </p>
      <div className="grid gap-4 sm:grid-cols-2 max-w-md">
        <div className="space-y-2">
          <Label htmlFor="defaultCapacity">Default Capacity</Label>
          <Input
            id="defaultCapacity"
            type="number"
            min={0}
            value={defaultCapacity}
            onChange={(e) => setDefaultCapacity(parseInt((e.target as HTMLInputElement).value, 10) || 0)}
          />
          <p className="text-xs text-muted-foreground">
            Default volunteers per week when no override is set
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="startDay">Week Starts On</Label>
          <Select
            value={String(startDay)}
            onValueChange={(v) => setStartDay(parseInt(v, 10))}
          >
            <SelectTrigger id="startDay">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {START_DAY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Saturday is typical for volunteer arrival schedules
          </p>
        </div>
      </div>
      {hasChanges && (
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Region Settings'}
        </Button>
      )}
    </div>
  )
}
